import type {
  CampaignPostCallMessageCreateRequestDto,
  CampaignPostCallMessageResponseDto,
  CampaignPostCallMessageUpdateRequestDto,
  PostCallMessageConditionDto,
} from '@shared/services/apiService/apiService';

export type PostCallMessageConditionOperator = PostCallMessageConditionDto['operator'];

export const POST_CALL_CONDITION_OPERATORS: PostCallMessageConditionOperator[] = [
  'equals',
  'not_equals',
  'contains',
  'exists',
  'not_exists',
];

export const POST_CALL_VALUE_OPERATORS: PostCallMessageConditionOperator[] = [
  'equals',
  'not_equals',
  'contains',
];

export const operatorNeedsValue = (operator: PostCallMessageConditionOperator): boolean =>
  POST_CALL_VALUE_OPERATORS.includes(operator);

export interface PostCallMessageConditionDraft {
  field: string;
  operator: PostCallMessageConditionOperator;
  value: string;
}

export interface PostCallMessageDraft {
  id?: string;
  isEnabled: boolean;
  order: number;
  template: string;
  conditions: PostCallMessageConditionDraft[];
}

export interface PostCallMessageValidationErrors {
  template?: string;
  conditions?: string;
}

export interface PostCallMessageValidationResult {
  isValid: boolean;
  errors: PostCallMessageValidationErrors;
}

export const createEmptyPostCallMessageDraft = (): PostCallMessageDraft => ({
  isEnabled: true,
  order: 0,
  template: '',
  conditions: [],
});

export const createEmptyPostCallCondition = (): PostCallMessageConditionDraft => ({
  field: '',
  operator: 'equals',
  value: '',
});

export const normalizePostCallMessage = (
  message: CampaignPostCallMessageResponseDto,
): PostCallMessageDraft => ({
  id: message.id,
  isEnabled: message.isEnabled,
  order: message.order,
  template: message.template ?? '',
  conditions: (message.conditions ?? []).map((condition) => ({
    field: condition.field ?? '',
    operator: condition.operator,
    value: condition.value ?? '',
  })),
});

export const sortPostCallMessages = (messages: PostCallMessageDraft[]): PostCallMessageDraft[] =>
  [...messages].sort((left, right) => left.order - right.order);

export const validatePostCallMessageDraft = (
  draft: PostCallMessageDraft,
): PostCallMessageValidationResult => {
  const errors: PostCallMessageValidationErrors = {};

  if (!draft.template.trim()) {
    errors.template = 'campaignPage.postCallMessages.validation.templateRequired';
  }

  const hasFieldlessCondition = draft.conditions.some((condition) => !condition.field.trim());

  if (hasFieldlessCondition) {
    errors.conditions = 'campaignPage.postCallMessages.validation.conditionFieldRequired';
  } else {
    const hasMissingValue = draft.conditions.some(
      (condition) => operatorNeedsValue(condition.operator) && !condition.value.length,
    );

    if (hasMissingValue) {
      errors.conditions = 'campaignPage.postCallMessages.validation.conditionValueRequired';
    }
  }

  return { isValid: Object.keys(errors).length === 0, errors };
};

const buildConditionsPayload = (
  conditions: PostCallMessageConditionDraft[],
): PostCallMessageConditionDto[] =>
  conditions.map((condition) => ({
    field: condition.field.trim(),
    operator: condition.operator,
    ...(operatorNeedsValue(condition.operator) ? { value: condition.value } : {}),
  }));

export const buildPostCallMessageCreateRequest = (
  draft: PostCallMessageDraft,
): CampaignPostCallMessageCreateRequestDto => ({
  isEnabled: draft.isEnabled,
  order: draft.order,
  template: draft.template.trim(),
  conditions: buildConditionsPayload(draft.conditions),
});

export const buildPostCallMessageUpdateRequest = (
  draft: PostCallMessageDraft,
): CampaignPostCallMessageUpdateRequestDto => ({
  isEnabled: draft.isEnabled,
  order: draft.order,
  template: draft.template.trim(),
  conditions: buildConditionsPayload(draft.conditions),
});
